export type MarkdownRun = { text: string; bold: boolean; italic: boolean; code?: true }

export type MarkdownItem = { runs: MarkdownRun[]; sub: { runs: MarkdownRun[] }[]; subKind: 'ul' | 'ol' | undefined }

export type MarkdownParagraph = { kind: 'paragraph'; runs: MarkdownRun[] }

export type MarkdownList = { kind: 'ul' | 'ol'; start: number; items: MarkdownItem[] }

export type MarkdownHeading = { kind: 'heading'; level: 1 | 2 | 3; runs: MarkdownRun[] }

export type MarkdownQuote = { kind: 'quote'; runs: MarkdownRun[] }

export type MarkdownTable = { kind: 'table'; header: MarkdownRun[][]; rows: MarkdownRun[][][] }

export type MarkdownBlock = MarkdownParagraph | MarkdownList | MarkdownHeading | MarkdownQuote | MarkdownTable

// The canvas's rule (`MARKDOWN_EMPHASIS` in `@k2/canvas/markup`): an asterisk next to a word on its outer side is text, a footnote mark.
const INLINE =
  /(?<![\p{L}\p{N}*])\*\*(?=\S)([^*]+?)(?<=\S)\*\*(?![\p{L}\p{N}*])|(?<![\p{L}\p{N}*])\*(?=\S)([^*]+?)(?<=\S)\*(?![\p{L}\p{N}*])|`([^`]+)`/gu

const plain = (text: string): MarkdownRun => ({ text, bold: false, italic: false })

const runsOf = (source: string): MarkdownRun[] => {
  const runs: MarkdownRun[] = []
  let last = 0
  INLINE.lastIndex = 0
  for (let match = INLINE.exec(source); match; match = INLINE.exec(source)) {
    if (match.index > last) runs.push(plain(source.slice(last, match.index)))
    if (match[1] !== undefined) runs.push({ text: match[1], bold: true, italic: false })
    else if (match[2] !== undefined) runs.push({ text: match[2], bold: false, italic: true })
    else runs.push({ ...plain(match[3] ?? ''), code: true })
    last = match.index + match[0].length
  }
  if (last < source.length) runs.push(plain(source.slice(last)))
  return runs
}

const LIST_LINE = /^(-|\d+\.) (.*)$/
const NESTED_LINE = /^ {2}(?:-|\d+\.) /

const HEADING = /^(#{1,3}) (.*)$/
const QUOTE = /^>\s?(.*)$/
const TABLE_ROW = /^\|.*\|$/
const TABLE_RULE = /^\|(\s*:?-+:?\s*\|)+$/

const kindOf = (marker: string): MarkdownList['kind'] => (marker === '-' ? 'ul' : 'ol')

const cellsOf = (row: string) =>
  row
    .slice(1, -1)
    .split(/(?<!\\)\|/)
    .map(cell => runsOf(cell.trim().replace(/\\\|/g, '|')))

export const parseMarkdown = (source: string): MarkdownBlock[] => {
  const blocks: MarkdownBlock[] = []
  let current: MarkdownBlock | undefined

  const flush = () => {
    if (current) blocks.push(current)
    current = undefined
  }

  const paragraph = (): MarkdownParagraph => {
    if (current?.kind === 'paragraph') return current
    flush()
    const block: MarkdownParagraph = { kind: 'paragraph', runs: [] }
    current = block
    return block
  }

  const list = (kind: MarkdownList['kind'], start: number): MarkdownList => {
    if (current?.kind === kind) return current
    flush()
    const block: MarkdownList = { kind, start, items: [] }
    current = block
    return block
  }

  const quoteBlock = (): MarkdownQuote => {
    if (current?.kind === 'quote') return current
    flush()
    const block: MarkdownQuote = { kind: 'quote', runs: [] }
    current = block
    return block
  }

  // Two trailing spaces are markdown's hard break: the next line of the paragraph starts on its own line.
  let hardBreak = false
  for (const raw of source.split('\n')) {
    const line = raw.replace(/\s+$/, '')
    const breaksAfter = /\S {2,}$/.test(raw)
    // CommonMark: a blank line between items keeps a loose list open.
    if (!line.trim()) {
      if (current?.kind !== 'ul' && current?.kind !== 'ol') flush()
      hardBreak = false
      continue
    }

    const trimmed = line.trim()
    const heading = HEADING.exec(trimmed)
    if (heading) {
      flush()
      blocks.push({ kind: 'heading', level: (heading[1] ?? '#').length as MarkdownHeading['level'], runs: runsOf(heading[2] ?? '') })
      hardBreak = false
      continue
    }

    const quote = QUOTE.exec(trimmed)
    if (quote) {
      const block = quoteBlock()
      block.runs = [...block.runs, ...(block.runs.length ? [plain('\n')] : []), ...runsOf(quote[1] ?? '')]
      hardBreak = false
      continue
    }

    if (TABLE_ROW.test(trimmed)) {
      if (current?.kind === 'table') {
        if (!TABLE_RULE.test(trimmed)) current.rows.push(cellsOf(trimmed))
      } else {
        flush()
        current = { kind: 'table', header: cellsOf(trimmed), rows: [] } satisfies MarkdownTable
      }
      hardBreak = false
      continue
    }

    const [, marker = '', text = ''] = LIST_LINE.exec(trimmed) ?? []
    if (marker === '') {
      const block = paragraph()
      block.runs = [...block.runs, ...(block.runs.length ? [plain(hardBreak ? '\n' : ' ')] : []), ...runsOf(line.trim())]
      hardBreak = breaksAfter
      continue
    }
    hardBreak = false

    const parent = current?.kind === 'ul' || current?.kind === 'ol' ? current.items.at(-1) : undefined
    if (parent && NESTED_LINE.test(line)) {
      parent.sub.push({ runs: runsOf(text) })
      parent.subKind = kindOf(marker)
      continue
    }

    list(kindOf(marker), Number.parseInt(marker, 10) || 1).items.push({ runs: runsOf(text), sub: [], subKind: undefined })
  }

  flush()
  return blocks
}

const runsText = (runs: MarkdownRun[]) => runs.map(run => run.text).join('')

const joined = (groups: MarkdownRun[][], separator: string) => groups.flatMap((runs, index) => (index ? [plain(separator), ...runs] : runs))

const blockRuns = (block: MarkdownBlock) => {
  if (block.kind === 'paragraph' || block.kind === 'heading' || block.kind === 'quote') return block.runs
  if (block.kind === 'table')
    return joined(
      [block.header, ...block.rows].map(row => joined(row, ' · ')),
      '\n'
    )
  return joined(
    block.items.flatMap(item => [item.runs, ...item.sub.map(sub => sub.runs)]),
    '\n'
  )
}

// A section as the runs a canvas layer can carry: every block becomes lines, and only bold and italic survive (G-29).
export const flatRuns = (source: string) => joined(parseMarkdown(source).map(blockRuns), '\n')

export const plainText = (source: string) => runsText(flatRuns(source)).trim()
